import React from 'react';
import { X, AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'success' | 'info';
  children?: React.ReactNode;
  loading?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  children,
  loading = false,
  size = 'medium'
}) => {
  if (!isOpen) return null;

  const getTypeConfig = () => {
    switch (type) {
      case 'danger':
        return {
          bgGradient: 'from-red-600 to-pink-600',
          icon: AlertTriangle,
          iconColor: 'text-red-600',
          buttonBg: 'bg-red-600 hover:bg-red-700',
          buttonRing: 'focus:ring-red-500'
        };
      case 'warning':
        return {
          bgGradient: 'from-amber-600 to-orange-600',
          icon: AlertCircle,
          iconColor: 'text-amber-600',
          buttonBg: 'bg-amber-600 hover:bg-amber-700',
          buttonRing: 'focus:ring-amber-500'
        };
      case 'success':
        return {
          bgGradient: 'from-green-600 to-emerald-600',
          icon: CheckCircle,
          iconColor: 'text-green-600',
          buttonBg: 'bg-green-600 hover:bg-green-700',
          buttonRing: 'focus:ring-green-500'
        };
      case 'info':
        return {
          bgGradient: 'from-blue-600 to-indigo-600',
          icon: Info,
          iconColor: 'text-blue-600',
          buttonBg: 'bg-blue-600 hover:bg-blue-700',
          buttonRing: 'focus:ring-blue-500'
        };
      default:
        return {
          bgGradient: 'from-slate-600 to-slate-700',
          icon: AlertTriangle,
          iconColor: 'text-slate-600',
          buttonBg: 'bg-slate-600 hover:bg-slate-700',
          buttonRing: 'focus:ring-slate-500'
        };
    }
  };

  const config = getTypeConfig();
  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className={`bg-white rounded-2xl shadow-2xl w-full ml-64 ${
          size === 'small' ? 'max-w-sm' :
          size === 'large' ? 'max-w-4xl' :
          'max-w-md'
        }`}>
          {/* Modal Header */}
          <div className={`bg-gradient-to-r ${config.bgGradient} px-8 py-6 sticky top-0 z-10`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <IconComponent className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{title}</h3>
                  <p className="text-white/80 text-sm mt-0.5">Please confirm your action</p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={loading}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors disabled:opacity-50"
              >
                <X className="text-white" size={24} />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-8 space-y-6">
            <div className={`bg-slate-50 border border-slate-200 rounded-xl p-4`}>
              <div className="flex items-start gap-3">
                <IconComponent className={`${config.iconColor} mt-0.5`} size={20} />
                <div>
                  <p className="text-sm text-slate-900 font-medium">{message}</p>
                </div>
              </div>
            </div>

            {children && (
              <div className="bg-slate-50 rounded-xl p-4">
                {children}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`inline-flex items-center gap-2 px-6 py-3 ${config.buttonBg} text-white font-medium rounded-xl focus:outline-none focus:ring-2 ${config.buttonRing} focus:ring-offset-2 transition-all shadow-sm disabled:opacity-50`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Processing...
                </>
              ) : (
                <>
                  <IconComponent size={18} />
                  {confirmText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;